import React, { useRef, useState } from 'react';
import { PasswordCallback } from '@forgerock/javascript-sdk';
import { useEffectOnce } from 'Hooks';

export const CODE_LENGTH = 8;

interface Props {
  callback: PasswordCallback;
}

export const OneTimePassword: React.FC<Props> = ({ callback }) => {
  const refs = useRef<HTMLInputElement[]>([]);
  const [code, setCode] = useState<string[]>([]);

  useEffectOnce(() => {
    focusInput(0);
  });

  const updateCode = (newCode: string[]) => {
    setCode(newCode);
    callback.setPassword(newCode.join(''));
  };

  const focusInput = (index: number) => {
    if (index < 0 || index >= CODE_LENGTH) return;

    const input = refs.current[index];
    input.focus();
    input.select();
  };

  const handleChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputDigit = Number(event.currentTarget.value[0]);
    if (Number.isNaN(inputDigit)) return;

    const newCode = [...code];
    newCode[index] = String(inputDigit);

    updateCode(newCode);
    focusInput(index + 1);
  };

  const handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code !== 'Backspace') return;

    const newCode = [...code];
    newCode[index] = '';

    updateCode(newCode);
    focusInput(index - 1);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const newCode = event.clipboardData
      .getData('text/plain')
      .split('')
      .filter((char) => char !== ' ')
      .map(Number)
      .filter(Number.isInteger)
      .slice(0, CODE_LENGTH)
      .map(String);

    updateCode(newCode);
    focusInput(CODE_LENGTH - 1);
  };

  return (
    <div className="flex flex-row gap-2 justify-center">
      {[...Array(CODE_LENGTH)].map((_, index) => (
        <input
          key={`digit-${index}`}
          name="otp"
          type="text"
          inputMode="numeric"
          maxLength={1}
          onKeyDown={handleKeyDown(index)}
          onChange={handleChange(index)}
          onPaste={handlePaste}
          value={code[index] ?? ''}
          required={code.length === index}
          ref={(ref) => (refs.current[index] = ref as HTMLInputElement)}
          className="h-14 w-full border border-solid border-light-silver rounded text-center font-semibold text-2xl text-dark-gray"
        />
      ))}
    </div>
  );
};
